import { Box, Button, Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import EmptyTableRow from 'src/Component/EmptyTableRow'
import PaginatedTable, { HeadCellI, SortOrderI } from 'src/Component/PaginatedTable'
import DataRow from './DataRow'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import { errorToast, successToast } from 'src/utils/toastService'
import SearchInput from 'src/Component/SearchInput'
import AddModal from './AddModal'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import ViewModal from './ViewModal'
import EditModal from './EditModal'
import TabComponent from 'src/Component/TabComponent'
import { PRODUCT_TYPES } from 'src/utils/constants'
import { useDeleteProductMutation, useGetProductsByTypeQuery } from 'src/api/manageProductsApi'

const headCells: HeadCellI[] = [
  {
    id: 'id',
    isCheckBox: true,
    sortable: false,
    label: 'checkbox',
  },
  {
    id: 'created_at',
    sortable: true,
    label: 'Created On',
  },
  {
    id: 'title',
    sortable: true,
    label: 'Product Name',
  },
  {
    id: 'price',
    sortable: true,
    label: 'Price',
  },
  {
    id: 'unit',
    sortable: true,
    label: 'Unit',
  },
  {
    id: 'is_active',
    sortable: true,
    label: 'Status',
  },
  {
    id: 'id',
    sortable: false,
    label: 'Action',
    align: 'center',
  },
]

const Products = () => {
  // states
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0)
  const [page, setPage] = useState<number>(1)
  const [sortOrder, setSortOrder] = useState<SortOrderI>('asc')
  const [sortOrderBy, setSortOrderBy] = useState<string>('') // headCells[i].id
  const [openAddNewModal, setOpenAddNewModal] = useState(false)
  const [productViewModal, setProductViewModal] = useState({
    id: 0,
    open: false,
  })

  const [productDeleteConfirmation, setProductDeleteConfirmation] = useState({
    id: 0,
    open: false,
  })

  const [editProductModal, setEditProductModal] = useState({
    id: 0,
    open: false,
  })

  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [search, setSearch] = useState<string>('')

  // hooks
  const {
    data: PRODUCTS_DATA_RESPONSE,
    isFetching,
    refetch,
  } = useGetProductsByTypeQuery(
    {
      type: PRODUCT_TYPES[activeTabIndex],
      search,
      page,
      pageSize: 10,
      sortBy: (() => {
        if (sortOrder === 'asc') {
          return sortOrderBy
        } else {
          return `-${sortOrderBy}`
        }
      })(),
    },
    { refetchOnMountOrArgChange: true },
  )
  const [deleteProduct, { isLoading }] = useDeleteProductMutation()
  const dispatch = useDispatch<AppDispatch>()

  // functions
  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handleSorting = (_sortOrderBy: string) => {
    setSortOrder((prevState) => {
      return prevState === 'asc' ? 'desc' : 'asc'
    })
    setSortOrderBy(_sortOrderBy)
  }

  const handleProductViewModalClose = () => {
    setProductViewModal({
      id: 0,
      open: false,
    })
  }

  const handleViewDetailsClick = (id: number) => {
    setProductViewModal({
      id: id,
      open: true,
    })
  }

  const handleCloseDeleteConfirmation = () => {
    setProductDeleteConfirmation({
      id: 0,
      open: false,
    })
  }

  const handleOpenDeleteConfirmation = (id: number) => {
    setProductDeleteConfirmation({
      id: id,
      open: true,
    })
  }

  const handleEditProductModalClose = () => {
    setEditProductModal({
      id: 0,
      open: false,
    })
  }

  const handleEditProductClick = (id: number) => {
    setEditProductModal({
      id: id,
      open: true,
    })
  }

  const handleDeleteProduct = () => {
    deleteProduct(productDeleteConfirmation.id)
      .unwrap()
      .then((res) => {
        console.log(res)
        successToast('Product deleted successfully.')
        handleEditProductModalClose()
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete product.')
      })
      .finally(() => {
        handleCloseDeleteConfirmation()
      })
  }

  const handleSelectProduct = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds((prevState) => {
        return prevState.filter((productId) => productId !== id)
      })
    } else {
      setSelectedIds((prevState) => {
        return [...prevState, id]
      })
    }
  }

  const handleSelectAllClick = (checked: boolean) => {
    if (checked && PRODUCTS_DATA_RESPONSE?.results.length) {
      setSelectedIds(PRODUCTS_DATA_RESPONSE.results.map((product) => product.id))
    } else {
      setSelectedIds([])
    }
  }

  const handleDeleteSelected = () => {
    console.log(selectedIds)
    Promise.all(selectedIds.map((id) => deleteProduct(id).unwrap()))
      .then((res) => {
        console.log(res)
        successToast('Selected products deleted successfully.')
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete selected products.')
      })
  }

  const handleSearch = (value: string) => {
    setPage(1)
    setSearch(value)
  }

  const onAddProductSuccessCallback = () => {
    setOpenAddNewModal(false)
    refetch()
  }

  const onEditProductSuccessCallback = () => {
    handleEditProductModalClose()
    refetch()
  }

  const handleTabChange = (tabIndex: number) => {
    setActiveTabIndex(tabIndex)
    setPage(1);
    setSearch('')
  }

  // use effects
  useEffect(() => {
    if (isFetching || isLoading) {
      dispatch(startPageLoader())
    } else {
      dispatch(stopPageLoader())
    }
  }, [isFetching, isLoading])

  useEffect(() => {
    setSelectedIds([])
  }, [PRODUCTS_DATA_RESPONSE])


  return (
    <Box className='pageContainer'>
      <Grid container>
        <Grid md={8} sm={12} xs={12}>
          {' '}
        </Grid>
        <Grid md={4} sm={12} xs={12}>
          <Box>
            <SearchInput hasDebounce onChange={handleSearch} value={search} />
          </Box>
        </Grid>
      </Grid>

      <Grid mt={10} container justifyContent='space-between'>
        <Grid item>
          {' '}
          <TabComponent
            options={PRODUCT_TYPES}
            activeIndex={activeTabIndex}
            onTabIndexChange={handleTabChange}
          />{' '}
        </Grid>
        <Grid item>
          {' '}
          <Button
            variant='contained'
            color='primary'
            type='button'
            onClick={() => setOpenAddNewModal(true)}
          >
            ADD NEW
          </Button>
        </Grid>
      </Grid>

      <Box mt={2}>
        {PRODUCTS_DATA_RESPONSE && (
          <PaginatedTable
            title={PRODUCT_TYPES[activeTabIndex]}
            headCells={headCells}
            currentPage={page}
            onPageChange={handlePageChange}
            totalPages={Math.ceil(PRODUCTS_DATA_RESPONSE?.count / 10)}
            onSortClick={handleSorting}
            onSelectAllClick={handleSelectAllClick}
            isAllSelected={PRODUCTS_DATA_RESPONSE.results.length === selectedIds.length}
            numSelected={selectedIds.length}
            onDeleteSelected={handleDeleteSelected}
          >
            <>
              {PRODUCTS_DATA_RESPONSE?.results?.length === 0 && (
                <EmptyTableRow cellCount={headCells.length} />
              )}
              {PRODUCTS_DATA_RESPONSE?.results?.map((product) => {
                return (
                  <DataRow
                    key={product.id}
                    id={product.id}
                    date={product.created_at}
                    title={product.title}
                    price={product.price}
                    unit={product.unit}
                    isActive={product.is_active}
                    selected={selectedIds.includes(product.id)}
                    onViewDetailsClick={handleViewDetailsClick}
                    onDeleteClick={handleOpenDeleteConfirmation}
                    onSelect={handleSelectProduct}
                    onEditClick={handleEditProductClick}
                  />
                )
              })}
            </>
          </PaginatedTable>
        )}
      </Box>

      <AddModal
        open={openAddNewModal}
        onClose={() => setOpenAddNewModal(false)}
        successCallback={onAddProductSuccessCallback}
      />
      {!!productViewModal.id && (
        <ViewModal
          id={productViewModal.id}
          open={productViewModal.open}
          onClose={handleProductViewModalClose}
        />
      )}

      <ConfirmationBox
        title='Are you sure you want to Delete this product.'
        open={productDeleteConfirmation.open}
        onReject={handleCloseDeleteConfirmation}
        onAccept={handleDeleteProduct}
      />

      {!!editProductModal.id && (
        <EditModal
          id={editProductModal.id}
          open={editProductModal.open}
          onClose={handleEditProductModalClose}
          successCallback={onEditProductSuccessCallback}
          onDeleteClick={handleOpenDeleteConfirmation}
        />
      )}
    </Box>
  )
}

export default Products
